/**
 * Photoshop "Type Options → Small Caps" — faux small-caps: строчные → uppercase
 * × `smallCapSize`. Заглавные сохраняют размер. Кеrning внутри сегмента
 * сохраняется через один `font.getPath`; на границе теряется (приемлемо при
 * смене case).
 */
export interface TextCapsSegment {
  text: string;
  fontSizeScale: number;
}

export interface FontGlyphLookup {
  charToGlyphIndex(char: string): number;
  tables?: {
    gsub?: {
      features?: ReadonlyArray<{ tag: string }>;
    };
  };
}

/**
 * Photoshop при `fontCaps=1` пропускает faux scaling если шрифт сам имеет
 * real small caps:
 * 1. Шрифт без lowercase (Copperplate): lowercase → .notdef или uppercase glyph.
 * 2. Шрифт с designed small caps + OpenType `smcp` feature (Trajan Pro 3):
 *    lowercase glyph IS small cap; faux × 0.7 даст double-shrink (~60%).
 *
 * Height-based heuristic ненадёжен (Inter x-height = 0.52em попадает в "designed
 * small caps" range), поэтому используем GSUB `smcp` flag — официальный признак.
 */
export function shouldApplySmallCaps(text: string, font: FontGlyphLookup | undefined): boolean {
  if (!font) return true;
  const hasSmcp = font.tables?.gsub?.features?.some(f => f.tag === 'smcp') ?? false;
  if (hasSmcp) return false;
  for (const ch of text) {
    const upper = ch.toUpperCase();
    if (ch === upper) continue;
    const lowerIdx = font.charToGlyphIndex(ch);
    const upperIdx = font.charToGlyphIndex(upper);
    if (lowerIdx === upperIdx || lowerIdx === 0) continue;
    return true;
  }

  return false;
}

export function applyFontCapsSegments(
  text: string,
  fontCaps: number | undefined,
  smallCapSize: number,
  font?: FontGlyphLookup,
): TextCapsSegment[] {
  if (fontCaps == null || fontCaps === 0) return [{ text, fontSizeScale: 1 }];
  if (fontCaps === 2) return [{ text: text.toUpperCase(), fontSizeScale: 1 }];

  if (font && !shouldApplySmallCaps(text, font)) {
    return [{ text, fontSizeScale: 1 }];
  }

  const segments: TextCapsSegment[] = [];
  let bufText = '';
  let bufScale: number | null = null;

  for (const ch of text) {
    const upper = ch.toUpperCase();
    const lower = ch.toLowerCase();
    const isLetter = upper !== lower;
    const isLowerCaseLetter = isLetter && ch === lower;

    let targetScale: number;
    let targetText: string;
    if (!isLetter) {
      // Non-letter наследует scale предыдущего сегмента (пробел между small caps остаётся small).
      targetScale = bufScale ?? 1;
      targetText = ch;
    } else if (isLowerCaseLetter) {
      targetScale = smallCapSize;
      targetText = upper;
    } else {
      targetScale = 1;
      targetText = ch;
    }

    if (bufScale === null) {
      bufText = targetText;
      bufScale = targetScale;
    } else if (targetScale === bufScale) {
      bufText += targetText;
    } else {
      segments.push({ text: bufText, fontSizeScale: bufScale });
      bufText = targetText;
      bufScale = targetScale;
    }
  }

  if (bufScale !== null) segments.push({ text: bufText, fontSizeScale: bufScale });
  return segments;
}
